import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { STATUS_LABELS, STATUS_COLORS } from "../lib/api";
import { Bell, LogOut, Menu as MenuIcon, ChevronRight, LayoutDashboard, Briefcase, Users, Store, Map as MapIcon2, Wallet, Bot, Settings, X, Rocket } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import AccountMenu from "./AccountMenu";

export function StatusBadge({ status }) {
  return (
    <Badge data-testid={`status-badge-${status}`} className={`border ${STATUS_COLORS[status] || ""} font-medium`}>
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}

export function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6 animate-fade-up">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs tracking-[0.2em] uppercase text-zinc-400">{label}</span>
        {Icon && <Icon className={`h-5 w-5 ${accent ? "text-[#00E676]" : "text-zinc-500"}`} />}
      </div>
      <div className="text-3xl font-display font-bold tracking-tight">{value}</div>
    </div>
  );
}

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); } catch {}
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  const unread = items.filter((i) => !i.read).length;
  const markRead = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <div className="relative">
      <button data-testid="notification-bell" onClick={() => { setOpen(!open); if (!open) markRead(); }}
        className="relative p-2 rounded-full hover:bg-white/5 transition-colors">
        <Bell className="h-5 w-5" />
        {unread > 0 && <span data-testid="notification-count" className="absolute -top-0.5 -right-0.5 bg-[#00E676] text-black text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{unread}</span>}
      </button>
      {open && (
        <>
          {/* Backdrop: solo mobile, chiude toccando fuori. */}
          <div className="fixed inset-0 z-40 bg-black/40 sm:hidden" onClick={() => setOpen(false)} data-testid="notification-backdrop" />
          <div
            data-testid="notification-panel"
            className="fixed left-2 right-2 top-[68px] z-50 max-w-[calc(100vw-1rem)] max-h-[70vh] overflow-y-auto overflow-x-hidden overscroll-contain rounded-2xl bg-[#1C1C1F] border border-white/10 shadow-2xl
                       sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 sm:max-w-[calc(100vw-1rem)] sm:max-h-96 sm:rounded-lg"
            style={{ paddingRight: "env(safe-area-inset-right)", paddingLeft: "env(safe-area-inset-left)" }}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between gap-2 p-3 border-b border-white/10 bg-[#1C1C1F] font-semibold text-sm">
              <span>Notifiche</span>
              <button data-testid="notification-close" onClick={() => setOpen(false)} className="p-1 -mr-1 rounded-full hover:bg-white/10 text-zinc-400"><X className="h-4 w-4" /></button>
            </div>
            {items.length === 0 && <div className="p-4 text-sm text-zinc-500">Nessuna notifica</div>}
            {items.map((n) => (
              <div key={n.id} className="p-3 border-b border-white/5 text-sm text-zinc-300 break-words">
                {n.title && <div className="text-[#00E676] font-semibold mb-0.5">{n.title}</div>}
                {n.message}
                <div className="text-[10px] text-zinc-500 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString("it-IT") : ""}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const NAV_GROUP_DEFS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "operativita", label: "Operatività", icon: Briefcase },
  { id: "gestione", label: "Gestione", icon: Users },
  { id: "catalogo", label: "Catalogo & Offerta", icon: Store },
  { id: "mappa", label: "Mappa & Eventi", icon: MapIcon2 },
  { id: "finanza", label: "Finanza", icon: Wallet },
  { id: "crescita", label: "Marketing & Crescita", icon: Rocket },
  { id: "ai", label: "Intelligenza Artificiale", icon: Bot },
  { id: "impostazioni", label: "Impostazioni", icon: Settings },
];

// Mappa chiave voce -> gruppo (coerente tra i ruoli). Chiavi non mappate -> "operativita".
const KEY_TO_GROUP = {
  analytics: "dashboard", overview: "dashboard", stats: "dashboard", leaderboard: "dashboard",
  lavoro: "operativita", orders: "operativita", prenotazioni: "operativita",
  deliveries: "operativita", available: "operativita", market: "operativita",
  quotes: "operativita", bookings: "operativita", requests: "operativita",
  roadmap: "gestione", users: "gestione", professionals: "gestione", riders: "gestione", b2b: "gestione",
  businesses: "gestione", mercato: "gestione", verification: "gestione", reviews: "gestione",
  servizi: "catalogo", menu: "catalogo", categories: "catalogo", catalog: "catalogo", booking: "catalogo",
  opscatalog: "catalogo",
  geoaudit: "catalogo",
  events: "catalogo",
  map: "mappa", livemap: "mappa", mapadmin: "mappa",
  commissions: "finanza", liquidazioni: "finanza", wallet: "finanza", earnings: "finanza", subscription: "finanza", economia: "finanza",
  experiences: "ai", assistant: "ai", assistente: "ai", aiop: "ai",
  profile: "impostazioni", notify: "impostazioni",
  missions: "crescita", livelli: "crescita", rewards: "crescita", growth: "crescita", passports: "crescita",
  opportunities: "crescita", game: "crescita",
  deals: "crescita", advertising: "crescita", visibility: "crescita", offer: "crescita", coupons: "crescita",
};

function buildNavGroups(nav) {
  const byGroup = {};
  nav.forEach((n) => {
    const g = KEY_TO_GROUP[n.key] || "operativita";
    (byGroup[g] = byGroup[g] || []).push(n);
  });
  return NAV_GROUP_DEFS.filter((d) => (byGroup[d.id] || []).length).map((d) => ({ def: d, items: byGroup[d.id] }));
}

// Sidebar data-driven: gruppi espandibili (accordion multi-open). Gruppo con 1 sola voce = link piatto.
function SidebarNav({ nav, role, onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const activeRef = useRef(null);
  const groups = useMemo(() => buildNavGroups(nav), [nav]);
  const activeGroupId = useMemo(() => {
    const item = nav.find((n) => n.path === location.pathname);
    return item ? (KEY_TO_GROUP[item.key] || "operativita") : null;
  }, [nav, location.pathname]);
  const storeKey = `fuma_nav_open_${role || "x"}`;
  const [open, setOpen] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storeKey) || "{}"); } catch { return {}; }
  });
  useEffect(() => {
    if (activeGroupId) setOpen((o) => (o[activeGroupId] ? o : { ...o, [activeGroupId]: true }));
  }, [activeGroupId]);
  useEffect(() => { try { localStorage.setItem(storeKey, JSON.stringify(open)); } catch { /* noop */ } }, [open, storeKey]);
  useEffect(() => { if (activeRef.current) activeRef.current.scrollIntoView({ block: "nearest" }); }, [location.pathname]);

  const go = (n) => { navigate(n.path); onNavigate && onNavigate(); };

  const renderLeaf = (n, sub) => {
    const isActive = location.pathname === n.path;
    const Icon = n.icon;
    return (
      <button key={n.path} data-testid={`nav-${n.key}`} ref={isActive ? activeRef : null} onClick={() => go(n)}
        className={`w-full flex items-center gap-3 rounded-md text-sm transition-colors text-left ${sub ? "px-3 py-1.5" : "px-3 py-2"} ${isActive ? "bg-[#00E676] text-black font-semibold shadow-[0_0_12px_rgba(0,230,118,0.15)]" : "text-zinc-400 hover:bg-white/5 hover:text-zinc-100"}`}>
        <Icon className="h-4 w-4 shrink-0" /> <span className="truncate">{n.label}</span>
      </button>
    );
  };

  return (
    <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1 nx-scroll" data-testid="sidebar-nav">
      {groups.map(({ def, items }) => {
        if (items.length === 1) return renderLeaf(items[0], false);
        const expanded = !!open[def.id];
        const childActive = items.some((n) => n.path === location.pathname);
        const GIcon = def.icon;
        return (
          <div key={def.id}>
            <button data-testid={`nav-group-${def.id}`} aria-expanded={expanded}
              onClick={() => setOpen((o) => ({ ...o, [def.id]: !o[def.id] }))}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors hover:bg-white/5 hover:text-zinc-100 ${childActive ? "text-white" : "text-zinc-400"}`}>
              <span className="flex items-center gap-3 min-w-0"><GIcon className="h-5 w-5 shrink-0" /> <span className="truncate font-medium">{def.label}</span></span>
              <ChevronRight className={`h-4 w-4 text-zinc-500 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`} />
            </button>
            {expanded && (
              <div className="pl-6 mt-1 mb-2 space-y-1 relative before:absolute before:left-[13px] before:top-1 before:bottom-1 before:w-px before:bg-white/10 nx-accordion-in">
                {items.map((n) => renderLeaf(n, true))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export function DashboardLayout({ title, nav, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <div className="min-h-screen flex bg-[#0A0A0C] grain">
      <aside
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        className={`fixed lg:sticky top-0 z-40 h-[100dvh] lg:h-screen w-64 bg-[#1C1C1F] border-r border-white/10 flex flex-col transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-6 border-b border-white/10 shrink-0">
          <div className="font-display font-black text-lg text-[#00E676]">Fuma c'anduma</div>
          <div className="text-xs text-zinc-500 mt-1">{title}</div>
        </div>
        <SidebarNav nav={nav} role={user?.role} onNavigate={() => setMobileOpen(false)} />
        <div className="p-3 border-t border-white/10 shrink-0">
          <div className="px-4 py-2 text-sm">
            <div className="font-medium truncate">{user?.name}</div>
            <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          </div>
          <Button data-testid="logout-btn" variant="ghost" onClick={() => { logout(); navigate("/login"); }}
            className="w-full justify-start text-zinc-400 hover:text-white">
            <LogOut className="h-4 w-4 mr-2" /> Esci
          </Button>
        </div>
      </aside>
      {mobileOpen && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-black/60 border-b border-white/10 px-4 lg:px-8 py-4 flex items-center justify-between">
          <button data-testid="mobile-menu-toggle" className="lg:hidden" onClick={() => setMobileOpen(true)}><MenuIcon className="h-6 w-6" /></button>
          <h1 className="font-display font-bold text-xl tracking-tight">{title}</h1>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <AccountMenu />
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 relative z-10">{children}</main>
      </div>
    </div>
  );
}

export function geoToXY(lat, lng) {
  const minLat = 45.05, maxLat = 45.30, minLng = 7.50, maxLng = 7.80;
  if (lat == null || lng == null) return { x: 50, y: 50 };
  const x = Math.max(2, Math.min(98, ((lng - minLng) / (maxLng - minLng)) * 100));
  const y = Math.max(2, Math.min(98, ((maxLat - lat) / (maxLat - minLat)) * 100));
  return { x, y };
}

export function SimpleMap({ points = [], className = "" }) {
  return (
    <div data-testid="simulated-map" className={`relative bg-[#141416] border border-white/10 rounded-lg overflow-hidden ${className}`}
      style={{ minHeight: 240, backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "28px 28px" }}>
      <div className="absolute top-3 left-3 text-xs text-zinc-500 uppercase tracking-widest">Mappa (simulata)</div>
      {points.map((p, i) => (
        <div key={i} className="absolute flex flex-col items-center" style={{ left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%,-50%)" }}>
          <div className={`h-3 w-3 rounded-full ${p.color || "bg-[#00E676]"} ring-4 ring-black/40`} />
          <span className="text-[10px] mt-1 bg-black/70 px-1.5 py-0.5 rounded whitespace-nowrap">{p.label}</span>
        </div>
      ))}
    </div>
  );
}
